import React from 'react';
import styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    /** Optional label shown above the fallback UI (e.g. the route or feature name). */
    label?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Catches render-time errors in the subtree and renders a localized fallback UI
 * instead of letting the whole app tree unmount with a blank white screen.
 *
 * Reset by navigating away from the route (the boundary re-mounts per route).
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        // Keep a console trail so devs can diagnose. Replace with remote logger when available.
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary] caught error', { error, info, label: this.props.label });
    }

    private handleReload = (): void => {
        this.setState({ hasError: false, error: null });
    };

    render(): React.ReactNode {
        if (!this.state.hasError) return this.props.children;
        return (
            <div className={styles.container} role="alert">
                <h2 className={styles.title}>Something went wrong</h2>
                <p className={styles.message}>
                    {this.props.label
                        ? `We hit an unexpected error loading ${this.props.label}.`
                        : 'We hit an unexpected error rendering this page.'}
                </p>
                <button type="button" className={styles.button} onClick={this.handleReload}>
                    Try again
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;
